import { appModule, type AppModule } from '@overseer/contracts';

const KEY = 'overseer.defaultModule';

/**
 * Which application to open after signing in.
 *
 * This browser's copy. The person's choice is kept on the server too and wins
 * when it answers (`preferences.ts`); this copy is what the landing reads when
 * the server holds nothing or cannot be reached.
 *
 * ## Every read can fail, and all three ways are real
 *
 * - **Not set** — a first sign-in, or somebody who has never chosen.
 * - **Names a module the user no longer has** — a revoked group. Ignoring it is
 *   what stops a redirect loop into a module the server will not serve.
 * - **Unreadable** — a private window, cleared site data, or a browser
 *   configured to block storage, where the accessor itself throws.
 *
 * All three answer "no default", and the caller shows the picker. **A storage
 * failure must never block signing in**, which is why every access is wrapped.
 */
export const readDefaultModule = (available: AppModule[]): AppModule | null => {
  // Read inside the try, returned outside it: a `catch` that fell through to
  // shared code would have to invent a value for `stored`, and "storage threw"
  // and "nothing stored" reach the same answer by different routes.
  const stored = ((): string | null => {
    try {
      return window.localStorage.getItem(KEY);
    } catch {
      // Storage disabled or unavailable. No default is a correct answer.
      return null;
    }
  })();

  if (stored === null) {
    return null;
  }

  const parsed = appModule.safeParse(stored);

  // Parsed AND still granted. A stored `hotel` for a user whose group was
  // revoked must not send them anywhere — the server would refuse, and the
  // symptom would be a bounce the person cannot explain.
  return parsed.success && available.includes(parsed.data) ? parsed.data : null;
};

export const writeDefaultModule = (module: AppModule): void => {
  try {
    window.localStorage.setItem(KEY, module);
  } catch {
    // Nothing to do and nothing worth saying: the user picked a module and it
    // opened. Only the memory of the choice is lost.
  }
};

export const clearDefaultModule = (): void => {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // See above.
  }
};
